import React, { Component } from 'react';
import { string, func, bool } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { lazyLoadWithDimensions } from '../../util/contextHelpers';
import { LINE_ITEM_DAY, LINE_ITEM_NIGHT, propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser, isUserSeller } from '../../util/data';

import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { types as sdkTypes } from '../../util/sdkLoader';
import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';
import config from '../../config';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const { Money } = sdkTypes;

const priceData = (price, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

class ListingImage extends Component {
  render() {
    return <ResponsiveImage {...this.props} />;
  }
}
const LazyImage = lazyLoadWithDimensions(ListingImage, { loadAfterInitialRendering: 3000 });

export const ListingCardComponent = props => {
  const {
    className,
    rootClassName,
    intl,
    currentUser,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
  } = props;
  const isLoggedIn = !!currentUser;
  const isSeller = isUserSeller(currentUser);

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
    // Brand minimum order (from seller profile)
  const brandMinObj =
    author?.attributes?.profile?.publicData?.storeMinimumOrderAmount || null;

  const brandMinMoney =
    brandMinObj && typeof brandMinObj.amount === 'number'
      ? new Money(brandMinObj.amount, brandMinObj.currency || config.currency)
      : null;

  const brandMinFormatted =
    brandMinMoney && priceData(brandMinMoney, intl).formattedPrice;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = config.listing;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // Determine price display logic based on authentication
  const msrpPriceRaw = publicData?.msrpPrice && publicData.msrpPrice.amount && publicData.msrpPrice.currency
    ? new Money(publicData.msrpPrice.amount, publicData.msrpPrice.currency)
    : null;
  const formattedMsrpPriceAsMoney = msrpPriceRaw && priceData(msrpPriceRaw, intl);
  const formattedPriceMoney = priceData(price, intl);

  // Decide which price to show as main and which as secondary
  let mainPrice = null;
  let secondaryPrice = null;

  // Buyers: wholesale (main) + msrp (secondary)
  // Sellers + public: msrp only (same as public)
  if (currentUser && !isSeller) {
    mainPrice = formattedPriceMoney;               // wholesale
    secondaryPrice = formattedMsrpPriceAsMoney;    // msrp
  } else {
    mainPrice = formattedMsrpPriceAsMoney || formattedPriceMoney; // msrp (fallback)
    secondaryPrice = null;
  }


  const unitType = config.lineItemUnitType;
  const isNightly = unitType === LINE_ITEM_NIGHT;
  const isDaily = unitType === LINE_ITEM_DAY;

  const unitTranslationKey = isNightly
    ? 'ListingCard.perNight'
    : isDaily
    ? 'ListingCard.perDay'
    : 'ListingCard.perUnit';

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.price}>
          {/* PUBLIC + SELLERS: show MSRP ONLY (one line, no duplicates) */}
          {(!isLoggedIn || isSeller) && mainPrice?.formattedPrice ? (
            <div className={css.publicMsrpRow}>
              <span className={css.publicMsrpLabel}>MSRP</span>
              <span className={css.publicMsrpValue}>{mainPrice.formattedPrice}</span>
            </div>
          ) : null}

          {/* BUYERS (logged in non-seller): wholesale + MSRP */}
          {isLoggedIn && !isSeller ? (
            <>
              <div className={css.priceValue} title={mainPrice?.priceTitle}>
                {mainPrice?.formattedPrice}
              </div>

              {secondaryPrice && (
                <span className={css.msrpPrice}>
                  <FormattedMessage
                    id="ListingCard.msrpPrice"
                    values={{
                      price: (
                        <span className={css.priceValue} title={secondaryPrice.priceTitle}>
                          {secondaryPrice.formattedPrice}
                        </span>
                      ),
                    }}
                  />
                </span>
              )}
            </>
          ) : null}

          {config.listing.showUnitTypeTranslations ? (
            <div className={css.perUnit}>
              <FormattedMessage id={unitTranslationKey} />
            </div>
          ) : null}
        </div>

        <div className={css.mainInfo}>
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>

          {showAuthorInfo && (
            <NamedLink
              name="ProfilePage"
              params={{ id: author.id.uuid, displayName: authorName }}
              className={css.brandRow}
              onClick={e => e.stopPropagation()} // so clicking brand doesn't trigger product link
            >
              <span className={css.brandName}>{authorName}</span>

              {brandMinFormatted && (
                <>
                  <span className={css.brandDot}>•</span>
                  <span className={css.brandMinValue}>{brandMinFormatted}</span>
                  <span className={css.brandMinSuffix}> min</span>
                </>
              )}
            </NamedLink>
          )}
        </div>

      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

const mapStateToProps = state => {
  return {
    currentUser: state.user.currentUser,
  };
};

const ListingCard = compose(
  connect(mapStateToProps),
  injectIntl
)(ListingCardComponent);

export default ListingCard;
